<template>
    <pendo-multiselect
        class="simple-select"
        :class="`${valueKey}-select`"
        :value="value"
        :options="options"
        :extra-label-width="60"
        :append-to-body="false"
        :allow-empty="false"
        :disabled="disabled"
        :searchable="searchable"
        :group-options-key="null"
        :preselect-first="preselectFirst"
        :value-key="valueKey"
        :sort-by="sortBy"
        :invalid="checkValidRule && !valid"
        :placeholder="localizedPlaceholder"
        :loading="loadingResources"
        :popper-options="popperOptions"
        label-key="name"
        min-trigger-width="80px"
        :taggable="allowCustomValue"
        :min-menu-width="minMenuWidth"
        :max-menu-width="maxMenuWidth"
        :max-menu-height="maxMenuHeight"
        @input-value-change="$emit('input-value-change', $event)"
        @tag="onCustomOptionSelect"
        @select="onOptionSelect"
        @highlighted-option-change="$emit('highlighted-option-change', $event)">
        <template
            v-if="$slots.header"
            #header>
            <slot name="header" />
        </template>
        <template #noData>
            <slot name="noData" />
        </template>
        <template
            v-if="hasCustomOption"
            #option="{ option, index }">
            <slot
                name="option"
                v-bind="{ option, index }" />
        </template>
        <template #aside>
            <slot name="aside" />
        </template>
        <template
            #selectedLabel="{ option }"
            v-if="$slots.selectedLabel">
            <slot
                name="selectedLabel"
                :option />
        </template>
    </pendo-multiselect>
</template>

<script>
import { PendoMultiselect } from '@pendo/components';

export default {
    name: 'SimpleSelect',
    components: {
        PendoMultiselect,
    },
    props: {
        value: {
            type: Object,
            default: null,
        },
        options: {
            type: Array,
            required: true,
        },
        allowCustomValue: {
            type: Boolean,
            default: false,
        },
        customValidator: {
            type: Boolean,
            default: null,
        },
        field: {
            type: [String, Array],
            required: true,
        },
        placeholder: {
            type: String,
            default: '',
        },
        preselectFirst: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        sortBy: {
            type: String,
            default: null,
        },
        searchable: {
            type: Boolean,
            default: null,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        loadingResources: {
            type: Boolean,
            default: false,
        },
        popperOptions: {
            type: Object,
            default: undefined,
        },
        minMenuWidth: {
            type: [String, Number],
            default: 160,
        },
        maxMenuWidth: {
            type: [String, Number],
            default: 400,
        },
        maxMenuHeight: {
            type: [String, Number],
            default: 302,
        },
    },
    emits: ['input-value-change', 'input', 'highlighted-option-change'],
    data () {
        return {
            localizedPlaceholder: '',
        };
    },
    computed: {
        valueKey () {
            return Array.isArray(this.field) ? this.field[0] : this.field;
        },
        valid () {
            if (this.customValidator != null) {
                return this.customValidator;
            }

            return this.value != null && this.value !== false;
        },
        hasCustomOption () {
            return this.$slots.option;
        },
    },
    created () {
        this.localizedPlaceholder = !this.placeholder ? this.$t('simpleSelect.Select option') : this.placeholder;
    },
    methods: {
        onOptionSelect (option) {
            this.$emit('input', { field: this.field, value: option });
        },
        onCustomOptionSelect (option) {
            this.$emit('input', {
                field: this.field,
                // match pattern used for preset options
                value: {
                    [this.field]: option,
                    name: option,
                },
            });
        },
    },
};
</script>
